import React, { useState, useEffect } from "react";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { fetchProjects, createProject, updateProject, deleteProject } from "../api/api";

const EMPTY_FORM = { name: "", is_active: true };

function errorMessage(err) {
  const detail = err.response?.data?.detail;
  return detail || "Something went wrong. Please try again.";
}

function Projects() {

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [rowBusy, setRowBusy] = useState(null);
  const [rowError, setRowError] = useState("");

  const loadProjects = async () => {
    setProjectsLoading(true);
    setProjectsError("");
    try {
      const data = await fetchProjects();
      setProjects(data);
    } catch (err) {
      setProjectsError(errorMessage(err));
    } finally {
      setProjectsLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.name.trim()) {
      setFormError("Project name is required.");
      return;
    }

    setSubmitting(true);
    try {
      await createProject({
        name: form.name.trim(),
        is_active: form.is_active,
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      await loadProjects();
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (project) => {
    setEditingId(project.id);
    setEditForm({ name: project.name, is_active: project.is_active });
    setRowError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const saveEdit = async (projectId) => {
    if (!editForm.name.trim()) {
      setRowError("Project name is required.");
      return;
    }

    setRowBusy(projectId);
    setRowError("");
    try {
      const updated = await updateProject(projectId, {
        name: editForm.name.trim(),
        is_active: editForm.is_active,
      });
      setProjects((prev) => prev.map((p) => (p.id === projectId ? updated : p)));
      setEditingId(null);
    } catch (err) {
      setRowError(errorMessage(err));
    } finally {
      setRowBusy(null);
    }
  };

  const handleDelete = async (project) => {
    if (!window.confirm(`Delete project "${project.name}"? Plots currently assigned to it will need a new project.`)) {
      return;
    }

    setRowBusy(project.id);
    setRowError("");
    try {
      await deleteProject(project.id);
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
    } catch (err) {
      setRowError(errorMessage(err));
    } finally {
      setRowBusy(null);
    }
  };

  return (

    <div>

      <Navbar />

      <Sidebar />

      <div className="admin-content">

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h1 style={{ marginTop: "0", marginBottom: 0 }}>Projects</h1>
          <button className="login-btn" style={{ width: "auto", padding: "10px 20px" }} onClick={() => setShowForm((prev) => !prev)}>
            {showForm ? "Cancel" : "+ Add Project"}
          </button>
        </div>

        {projectsError && <p className="login-error" style={{ color: "red" }}>{projectsError}</p>}
        {rowError && <p className="login-error" style={{ color: "red" }}>{rowError}</p>}

        {showForm && (
          <div className="section-card">
            <h3>New Project</h3>

            {formError && <p className="login-error" style={{ color: "red" }}>{formError}</p>}

            <form onSubmit={handleCreateProject}>
              <input
                type="text"
                name="name"
                placeholder="Project Name *"
                value={form.name}
                onChange={handleFormChange}
                required
              />

              <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleFormChange} />
                Active
              </label>

              <button type="submit" className="login-btn" style={{ width: "auto", padding: "8px 16px" }} disabled={submitting}>
                {submitting ? "Creating…" : "Create Project"}
              </button>
            </form>
          </div>
        )}

        {projectsLoading && <p>Loading projects…</p>}

        {!projectsLoading && (
          <div style={{ background: "white", borderRadius: "10px", overflow: "hidden", boxShadow: "0 0 10px rgba(0,0,0,0.1)" }}>

            <table className="plot-table" style={{ width: "100%", borderCollapse: "collapse" }}>

              <thead style={{ background: "#f5f5f5" }}>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>

                {projects.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: "15px" }}>No projects yet.</td></tr>
                )}

                {projects.map((project) => (
                  <tr key={project.id}>
                    {editingId === project.id ? (
                      <>
                        <td>
                          <input
                            type="text"
                            name="name"
                            value={editForm.name}
                            onChange={handleEditFormChange}
                            style={{ width: "100%" }}
                          />
                        </td>
                        <td>
                          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <input type="checkbox" name="is_active" checked={editForm.is_active} onChange={handleEditFormChange} />
                            Active
                          </label>
                        </td>
                        <td>{new Date(project.created_at).toLocaleDateString()}</td>
                        <td>
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button
                              type="button"
                              title="Save"
                              onClick={() => saveEdit(project.id)}
                              disabled={rowBusy === project.id}
                              style={{ background: "#2e7d32", color: "white", border: "none", borderRadius: "4px", width: "26px", height: "26px", cursor: "pointer" }}
                            >
                              ✓
                            </button>
                            <button
                              type="button"
                              title="Cancel"
                              onClick={cancelEdit}
                              disabled={rowBusy === project.id}
                              style={{ background: "#dc2626", color: "white", border: "none", borderRadius: "4px", width: "26px", height: "26px", cursor: "pointer" }}
                            >
                              ✕
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td><b>{project.name}</b></td>
                        <td>{project.is_active ? "✅ Active" : "🚫 Inactive"}</td>
                        <td>{new Date(project.created_at).toLocaleDateString()}</td>
                        <td>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button className="view-btn" onClick={() => startEdit(project)} disabled={rowBusy === project.id}>
                              Edit
                            </button>
                            <button
                              className="view-btn"
                              onClick={() => handleDelete(project)}
                              disabled={rowBusy === project.id}
                              style={{ background: "#dc2626", color: "white" }}
                            >
                              {rowBusy === project.id ? "…" : "Delete"}
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}

              </tbody>

            </table>

          </div>
        )}

      </div>

    </div>
  );
}

export default Projects;
